import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  WalteroCard,
  WalteroDataTable,
  WalteroTypography,
  WalteroSkeleton,
  WalteroStatusChip,
  WalteroButton
} from './waltero';
import billingService from '../services/billingService';
import {
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

/**
 * Invoice History Tab
 *
 * Displays billing history with invoice download functionality
 */
const InvoiceHistoryTab = ({ organizationId }) => {
  const theme = useTheme();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // Load invoices
  useEffect(() => {
    if (organizationId) {
      loadInvoices();
    } else {
      setLoading(false);
      setInvoices([]);
    }
  }, [organizationId]);

  const loadInvoices = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // For now, generate mock invoice data
      // In production, this would come from: await billingService.getInvoices(organizationId)
      const mockInvoices = generateMockInvoices();
      setInvoices(mockInvoices);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError(err.error || 'Failed to load invoice history');
    } finally {
      setLoading(false);
    }
  };

  // Generate mock invoice data for demonstration
  const generateMockInvoices = () => {
    const today = new Date();
    const invoices = [];

    for (let i = 0; i < 6; i++) {
      const invoiceDate = new Date(today);
      invoiceDate.setMonth(today.getMonth() - i);

      invoices.push({
        id: `inv_${Date.now()}_${i}`,
        number: `INV-2025-${String(6 - i).padStart(3, '0')}`,
        date: invoiceDate.toISOString(),
        dueDate: new Date(invoiceDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        amount: 1200.00, // $100/month * 12 months
        status: i === 0 ? 'pending' : 'paid',
        description: 'Annual Subscription - 100 sensors',
        currency: 'USD',
        pdfUrl: '#'
      });
    }

    return invoices;
  };

  // Handle invoice download
  const handleDownload = async (invoice) => {
    try {
      setDownloading(invoice.id);
      // In production: await billingService.downloadInvoice(organizationId, invoice.id)

      // Mock download delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Show success message
      console.log(`Downloaded invoice ${invoice.number}`);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      setError('Failed to download invoice');
    } finally {
      setDownloading(null);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status color and icon
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'paid':
        return {
          color: 'success',
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
          label: 'PAID'
        };
      case 'pending':
        return {
          color: 'warning',
          icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
          label: 'PENDING'
        };
      case 'overdue':
        return {
          color: 'error',
          icon: <WarningIcon sx={{ fontSize: 16 }} />,
          label: 'OVERDUE'
        };
      default:
        return {
          color: 'default',
          label: status.toUpperCase()
        };
    }
  };

  // Define table columns
  const columns = [
    {
      field: 'number',
      headerName: 'Invoice #',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {formatCurrency(params.value, params.row.currency)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = getStatusDisplay(params.value);
        return (
          <WalteroStatusChip
            label={status.label}
            color={status.color}
            icon={status.icon}
            size="small"
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Download Invoice PDF">
          <IconButton
            size="small"
            onClick={() => handleDownload(params.row)}
            disabled={downloading === params.row.id}
          >
            {downloading === params.row.id ? (
              <CircularProgress size={20} />
            ) : (
              <DownloadIcon />
            )}
          </IconButton>
        </Tooltip>
      )
    }
  ];

  if (loading) {
    return (
      <Box>
        <WalteroSkeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <WalteroTypography variant="h5" fontWeight="bold">
            Invoice History
          </WalteroTypography>
          <Typography variant="body2" color="textSecondary">
            View and download your billing statements
          </Typography>
        </Box>
        <Tooltip title="Refresh invoices">
          <IconButton onClick={loadInvoices}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Invoice Table */}
      {invoices.length === 0 ? (
        <WalteroCard sx={{ p: 4, textAlign: 'center' }}>
          <ReceiptIcon
            sx={{
              fontSize: 64,
              color: theme.palette.grey[400],
              mb: 2
            }}
          />
          <Typography variant="h6" gutterBottom>
            No Invoices Yet
          </Typography>
          <Typography color="textSecondary">
            Invoices will appear here after your first billing cycle
          </Typography>
        </WalteroCard>
      ) : (
        <>
          <WalteroDataTable
            rows={invoices}
            columns={columns}
            initialPageSize={10}
            pageSizeOptions={[5, 10, 25]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${theme.palette.divider}`
              }
            }}
          />

          {/* Summary Section */}
          <Box mt={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Total invoices: {invoices.length}
              </Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Chip
                label={`${invoices.filter(i => i.status === 'paid').length} Paid`}
                color="success"
                size="small"
              />
              <Chip
                label={`${invoices.filter(i => i.status === 'pending').length} Pending`}
                color="warning"
                size="small"
              />
              {invoices.some(i => i.status === 'overdue') && (
                <Chip
                  label={`${invoices.filter(i => i.status === 'overdue').length} Overdue`}
                  color="error"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* Export Options */}
          <Box mt={3} p={2} bgcolor={theme.palette.grey[50]} borderRadius={1}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Need a report for accounting?
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <WalteroButton size="small" variant="outlined">
                EXPORT TO CSV
              </WalteroButton>
              <WalteroButton size="small" variant="outlined">
                EMAIL STATEMENTS
              </WalteroButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default InvoiceHistoryTab;